import { Form, Formik, getIn } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import SaveBackdrop from '../../components/saveBackdrop';
import Screen from '../../components/screen';
import SplashWait from '../../components/splash-wait';
import { AppContext } from '../../contexts/app-context';
import { HtmlContent } from '../../global-component-styles';
import GlobalStyles from '../../global-styles';
import API from '../../utils/api';
import {
  apiReponseError, apiRequestError, checkUserAuthenticated, getSiteSetting, goToRoutePage,
  setWindowTitle
} from '../../utils/helper';
import {
  Box, Breadcrumbs, Button, FormControl, FormControlLabel, FormGroup, FormLabel, Grid, Link,
  Switch, TextField, Typography
} from '../../utils/material-ui-core';

// Default form value
const formDefaultValue = {
  sensorAdminLocked: false,
  sensorAdminLockMessage: "",
  sensorAdminBulletinTitle: "",
  sensorAdminBulletinMessage: "",
  sensorAdminBulletinStartDate: "",
  sensorAdminBulletinEndDate: "",
  iotServiceLocked: false,
  iotServiceLockMessage: "",
  whitelistUsersWhenAppLocked: "",
};

const formDefaultTouchValue = {
  sensorAdminLocked: false,
  sensorAdminLockMessage: false,
  sensorAdminBulletinTitle: false,
  sensorAdminBulletinMessage: false,
  sensorAdminBulletinStartDate: false,
  sensorAdminBulletinEndDate: false,
  iotServiceLocked: false,
  iotServiceLockMessage: false,
  whitelistUsersWhenAppLocked: false,
};

// Form validation schema
const formValidationSchema = Yup.object().shape({});

const ManageSiteSetting = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const formReference = useRef(null) as any;

  const { writeLog, userFunctionAccess, showAlert, isUserAuthenticated, refreshData } = useContext(AppContext);

  const [loading, setLoading] = useState<boolean>(false);
  const [initialFormValues, setInitialFormValues] = useState<SiteSetting>(formDefaultValue);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);

  // const validateEmail = (field) => {
  //   var regex = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}\b/i;
  //   return regex.test(field) ? true : false;
  // };

  // const validateMultipleEmailsCommaSeparated = (value) => {
  //   var result = value.split(",");
  //   let errorMessage = "";
  //   for (var i = 0; i < result.length; i++) {
  //     if (!validateEmail(result[i])) return false;
  //   }
  //   return true;
  // };

  const validateHtmlTags = (htmlString: string) => {
    let parser = new DOMParser();
    let doc = parser.parseFromString(htmlString, "application/xml");
    let errorNode = doc.querySelector("parsererror");

    if (errorNode) {
      let errorMessage = "";
      errorMessage += "<div style='color:red'>";
      errorMessage += errorNode.innerHTML;
      errorMessage += "</div>";
      return errorMessage;
    }

    return htmlString;
  };

  const validateNoHtmlTags = (htmlString: string) => {
    var reg = /<(.|\n)*?>/g;
    if (reg.test(htmlString) === true) {
      let errorMessage = "";
      errorMessage += "<div style='color:red'>";
      errorMessage += "HTML Tag are not allowed";
      errorMessage += "</div>";
      return errorMessage;
    }

    return htmlString;
  };
  const getData = async () => {
    setLoading(true);
    const apiResponse = await getSiteSetting();
    if (apiResponse.success) {
      const data = apiResponse.data;
      setInitialFormValues(data.siteSetting as SiteSetting);
      setLoading(false);
    } else {
      setLoading(false);
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      goToRoutePage("home");
      return;
    }
  };

  // Save
  const save = async (values: SiteSetting) => {
    if (!userFunctionAccess.userPermissionAdmin) {
      showAlert({ message: "Access Denied.", severity: "error" });
      goToRoutePage("home");
      return;
    }

    setSaveInProgress(true);

    const payload = {
      siteSetting: values,
    };

    try {
      await API.post("/api/admin/sitesetting", payload)
        .then(async (response) => {
          showAlert({ message: "Successfully saved.", severity: "success" });
          await refreshData("appstatus");
          setSaveInProgress(false);
          setInitialFormValues(response.data.siteSeting);
          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          showAlert({
            message: apiReponseError(error, "Failed to save app setting."),
            severity: "error",
          });
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  useEffect(() => {
    writeLog("Locked", "", "", "");
    setWindowTitle("App Setting");
    window.scrollTo(0, 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const initializePage = async () => {
      checkUserAuthenticated(isUserAuthenticated);
      if (isUserAuthenticated) {
        setLoading(false);
      }

      if (!userFunctionAccess.userPermissionAdmin) {
        showAlert({ message: "Access Denied.", severity: "error" });
        goToRoutePage("home");
        return;
      }

      getData();
    };

    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthenticated, userFunctionAccess.userPermissionAdmin]);

  return (
    <React.Fragment>
      {loading ? (
        <SplashWait />
      ) : (
        <Screen>
          <Box className={pageStyles.root}>
            <Grid container spacing={3}>
              <Grid item xs={9}>
                <Box className={globalStyles.bottomSpacer}>
                  <Breadcrumbs aria-label="breadcrumb">
                    <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                      Home
                    </Link>
                    <Link color="inherit" onClick={() => goToRoutePage("managesettings")} className={globalStyles.link}>
                      Manage Settings
                    </Link>
                    <Typography color="textPrimary">Site</Typography>
                  </Breadcrumbs>
                </Box>
              </Grid>
            </Grid>

            <SaveBackdrop open={saveInProgress} />

            <Formik
              innerRef={formReference}
              initialValues={initialFormValues}
              validationSchema={formValidationSchema}
              validateOnMount={true}
              initialTouched={formDefaultTouchValue}
              validateOnBlur={true}
              validateOnChange={true}
              onSubmit={(values: SiteSetting) => {
                save(values);
              }}
            >
              {({ values, errors, handleChange, handleSubmit, touched, handleBlur, isSubmitting, setFieldValue, resetForm }) => (
                <Form>
                  <Grid container spacing={3} className={globalStyles.formikContainer}>
                    <Grid item xs={12}>
                      <Box className={globalStyles.topic}>Sensor Admin</Box>
                    </Grid>

                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <FormControl className={globalStyles.formControl} fullWidth={true}>
                            <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                              Lock Application
                            </FormLabel>
                            <FormGroup>
                              <FormControlLabel
                                control={
                                  <Switch
                                    id="sensorAdminLocked"
                                    name="sensorAdminLocked"
                                    checked={values.sensorAdminLocked}
                                    onChange={(ev, checked) => {
                                      setFieldValue("sensorAdminLocked", checked);
                                      //handleIsInternalChanged(checked);
                                    }}
                                  />
                                }
                                label={""}
                                labelPlacement="end"
                              />
                            </FormGroup>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                          <TextField id="sensorAdminLockMessage" name="sensorAdminLockMessage" label="Lock Message" value={values.sensorAdminLockMessage} multiline rows={7} onChange={handleChange} onBlur={handleBlur} error={getIn(errors, "sensorAdminLockMessage")} helperText={getIn(errors, "sensorAdminLockMessage")} />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <h6>Preview</h6>

                      <HtmlContent dangerouslySetInnerHTML={{ __html: validateHtmlTags(values.sensorAdminLockMessage) }}></HtmlContent>
                    </Grid>

                    <Grid item xs={12}>
                      <Box p={1}></Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box className={globalStyles.topic}>IoT Service</Box>
                    </Grid>

                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <FormControl className={globalStyles.formControl} fullWidth={true}>
                            <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                              Lock Service
                            </FormLabel>
                            <FormGroup>
                              <FormControlLabel
                                control={
                                  <Switch
                                    id="iotServiceLocked"
                                    name="iotServiceLocked"
                                    checked={values.iotServiceLocked}
                                    onChange={(ev, checked) => {
                                      setFieldValue("iotServiceLocked", checked);
                                      //handleIsInternalChanged(checked);
                                    }}
                                  />
                                }
                                label={""}
                                labelPlacement="end"
                              />
                            </FormGroup>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                          <TextField id="iotServiceLockMessage" name="iotServiceLockMessage" label="Lock Message" value={values.iotServiceLockMessage} multiline rows={7} onChange={handleChange} onBlur={handleBlur} error={getIn(errors, "iotServiceLockMessage")} helperText={getIn(errors, "iotServiceLockMessage")} />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <h6>Preview</h6>
                      <HtmlContent dangerouslySetInnerHTML={{ __html: validateNoHtmlTags(values.iotServiceLockMessage) }}></HtmlContent>
                    </Grid>

                    <Grid item xs={12}>
                      <Box p={1}></Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box className={globalStyles.topic}>Bulletin</Box>
                    </Grid>

                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <TextField id="sensorAdminBulletinTitle" name="sensorAdminBulletinTitle" label="Title" value={values.sensorAdminBulletinTitle} onChange={handleChange} onBlur={handleBlur} error={getIn(errors, "sensorAdminBulletinTitle")} helperText={getIn(errors, "sensorAdminBulletinTitle")} />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField id="sensorAdminBulletinMessage" name="sensorAdminBulletinMessage" label="Message" value={values.sensorAdminBulletinMessage} multiline rows={7} onChange={handleChange} onBlur={handleBlur} error={getIn(errors, "sensorAdminBulletinMessage")} helperText={getIn(errors, "sensorAdminBulletinMessage")} />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField id="sensorAdminBulletinStartDate" name="sensorAdminBulletinStartDate" type="datetime-local" label="Start Date" value={values.sensorAdminBulletinStartDate} onChange={handleChange} onBlur={handleBlur} error={getIn(errors, "sensorAdminBulletinStartDate")} helperText={getIn(errors, "sensorAdminBulletinStartDate")} />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField id="sensorAdminBulletinEndDate" name="sensorAdminBulletinEndDate" type="datetime-local" label="End Date" value={values.sensorAdminBulletinEndDate} onChange={handleChange} onBlur={handleBlur} error={getIn(errors, "sensorAdminBulletinEndDate")} helperText={getIn(errors, "sensorAdminBulletinEndDate")} />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <h6>Preview</h6>
                      <HtmlContent dangerouslySetInnerHTML={{ __html: validateHtmlTags(values.sensorAdminBulletinTitle) }}></HtmlContent>
                      <HtmlContent dangerouslySetInnerHTML={{ __html: validateHtmlTags(values.sensorAdminBulletinMessage) }}></HtmlContent>
                    </Grid>

                    <Grid item xs={12}>
                      <Box p={1}></Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box className={globalStyles.topic}>Whitelist Users</Box>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField id="whitelistUsersWhenAppLocked" name="whitelistUsersWhenAppLocked" label="Email Ids" value={values.whitelistUsersWhenAppLocked} multiline rows={7} onChange={handleChange} onBlur={handleBlur} error={getIn(errors, "whitelistUsersWhenAppLocked")} helperText={getIn(errors, "whitelistUsersWhenAppLocked")} />
                      <Box>
                        <small>In the text box, enter an email address, type a comma followed by a space, then add another address.</small>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box p={1}></Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box p={1} display="flex" justifyContent="center">
                        <Button id="button-save" variant="contained" color="secondary" onClick={() => formReference.current.handleSubmit()} className={globalStyles.buttonDefault}>
                          Update
                        </Button>

                        <Button id="button-cancel" onClick={() => goToRoutePage("managesettings")} className={globalStyles.buttonDefault}>
                          <u>Cancel</u>
                        </Button>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box p={1}></Box>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Box>
        </Screen>
      )}
    </React.Fragment>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    menuActionContainer: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      margin: 0,
      backgroundColor: theme.palette.type === "dark" ? "transparent" : "transparent",
      padding: 0,
    },
    menuActionTitle: {
      fontSize: 20,
      fontWeight: "bolder",
      textTransform: "uppercase",
      letterSpacing: 2,
      color: theme.palette.type === "dark" ? theme.palette.grey[300] : theme.palette.grey[700],
      marginBottom: theme.spacing(1),
      textAlign: "center",
      height: "50px",
    },
    menuActionNote: {
      height: "95px",
      textAlign: "center",
      overflow: "auto",
    },
    menuActionImage: {
      paddingTop: 10,
      width: 60,
      height: 60,
      borderWidth: 0,
      borderStyle: "solid",
      borderColor: theme.palette.type === "dark" ? theme.palette.secondary.light : theme.palette.secondary.light,
      borderRadius: "4% 25%",
      marginBottom: theme.spacing(2),
      "& svg": {
        color: theme.palette.secondary.dark,
      },
      "& > :hover": {},
    },
    menuActionContent: {
      margin: "50px 20px 20px 20px",
      backgroundColor: theme.palette.type === "dark" ? "transparent" : "transparent",
      "& .MuiCardHeader-title": {
        fontSize: 24,
      },
      cursor: "pointer",
      transition: "all 0.5s ease-in-out 0s",
      borderRadius: "0%",
      [theme.breakpoints.down("xs")]: {
        borderWidth: 2,
        borderStyle: "solid",
        borderColor: theme.palette.type === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
      },
      "& > :hover": {
        backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
        transform: "scale(1.2)",
        transition: "all 0.8s ease-in-out 0s",
        [theme.breakpoints.down("xs")]: {
          transform: "scale(1)",
        },
        "& $menuActionImage": {
          "& svg": {
            color: theme.palette.primary.dark,
          },
          transition: "all 0.5s ease-in-out 0s",
        },
      },
    },
  })
);

export default ManageSiteSetting;
